/**
 * @engine/vfx — VFX preset builders (single-system group).
 *
 * Pure factories returning VFXGraphData, extracted from VFXGraphAsset.ts.
 * Re-exported from VFXGraphAsset so existing import paths are unchanged.
 */

import type { VFXGraphData } from '../types';
import { regenerateGraphIds } from './_shared';

/**
 * Create a firework VFX graph with preset nodes.
 *
 * Includes:
 * - Burst spawn (50-100 particles)
 * - Random sphere position with small radius
 * - Cone velocity (upward spread)
 * - Gravity and drag
 * - Color over life (orange -> red -> fade)
 * - Size over life (shrink)
 * - Quad output
 */

export function createWaterRippleGridVFX(
  name: string = "Water Ripple Grid",
): VFXGraphData {
  const template: VFXGraphData = {
    id: "water_ripple_grid",
    name: "Water Ripple Grid",
    nodes: [
      // Single burst spawn for 10000 particles
      {
        id: "spawn_grid",
        type: "vfx.spawn.single",
        position: { x: 50, y: 50 },
        inputValues: { count: 10000 },
        contextId: "ctx_spawn",
      },

      // === OPERATOR NODES FOR GRID POSITION (no contextId - operators are global) ===

      // Get particle index (0..9999)
      {
        id: "op_get_index",
        type: "vfx.operator.get_particle_index",
        position: { x: -250, y: 50 },
        inputValues: {},
      },
      // Convert index to float for math
      {
        id: "op_index_to_float",
        type: "vfx.operator.uint_to_float",
        position: { x: -250, y: 130 },
        inputValues: {},
      },
      // col = index % 100
      {
        id: "op_modulo",
        type: "vfx.operator.modulo",
        position: { x: -250, y: 210 },
        inputValues: { b: 100 },
      },
      // row = floor(index / 100)
      {
        id: "op_divide",
        type: "vfx.operator.divide",
        position: { x: -100, y: 210 },
        inputValues: { b: 100 },
      },
      {
        id: "op_floor",
        type: "vfx.operator.floor",
        position: { x: -100, y: 290 },
        inputValues: {},
      },
      // x = (col - 50) * 3
      {
        id: "op_col_offset",
        type: "vfx.operator.subtract",
        position: { x: -250, y: 370 },
        inputValues: { b: 50 },
      },
      {
        id: "op_col_scale",
        type: "vfx.operator.multiply",
        position: { x: -250, y: 450 },
        inputValues: { b: 3 },
      },
      // y = (row - 50) * 3
      {
        id: "op_row_offset",
        type: "vfx.operator.subtract",
        position: { x: -100, y: 370 },
        inputValues: { b: 50 },
      },
      {
        id: "op_row_scale",
        type: "vfx.operator.multiply",
        position: { x: -100, y: 450 },
        inputValues: { b: 3 },
      },
      // Compose vec3(x, y, 0)
      {
        id: "op_compose_pos",
        type: "vfx.operator.vec3_compose",
        position: { x: -175, y: 530 },
        inputValues: { z: 0 },
      },

      // === INIT CONTEXT NODES ===

      // Set position from operators
      {
        id: "init_position",
        type: "vfx.init.set_position",
        position: { x: 50, y: 50 },
        inputValues: {},
        contextId: "ctx_init",
      },
      // Very long lifetime (effectively infinite)
      {
        id: "init_lifetime",
        type: "vfx.init.lifetime",
        position: { x: 50, y: 150 },
        inputValues: { lifetime: 999999 },
        contextId: "ctx_init",
      },
      // Small size for dots
      {
        id: "init_size",
        type: "vfx.init.size",
        position: { x: 50, y: 250 },
        inputValues: { size: 2 },
        contextId: "ctx_init",
      },
      // Blue HDR gradient color (r/g/b values > 1 for HDR bloom)
      {
        id: "init_color",
        type: "vfx.init.color_random",
        position: { x: 50, y: 350 },
        inputValues: {
          gradient: {
            stops: [
              { t: 0, color: { r: 0.4, g: 0.8, b: 4.0, a: 1 } }, // Bright cyan HDR
              { t: 0.3, color: { r: 0.2, g: 0.5, b: 3.0, a: 1 } }, // Blue HDR
              { t: 0.6, color: { r: 0.3, g: 0.7, b: 3.5, a: 1 } }, // Light blue HDR
              { t: 1, color: { r: 0.1, g: 0.4, b: 2.5, a: 1 } }, // Deep blue HDR
            ],
          },
        },
        contextId: "ctx_init",
      },
      // Zero velocity (particles stay in place)
      {
        id: "init_velocity",
        type: "vfx.init.velocity_direction",
        position: { x: 50, y: 450 },
        inputValues: {
          direction: { x: 0, y: 0, z: 0 },
          speed: 0,
          spread: 0,
        },
        contextId: "ctx_init",
      },
      // Wobble - small noise jitter
      {
        id: "update_wobble",
        type: "vfx.update.wobble",
        position: { x: 50, y: 50 },
        inputValues: {
          amplitude: 0.15,
          frequency: 1.5,
          speed: 2.5,
        },
        contextId: "ctx_update",
      },
      // Multiple ripple sources
      {
        id: "update_ripple",
        type: "vfx.update.multi_ripple",
        position: { x: 50, y: 150 },
        inputValues: {
          count: 4,
          radius: 120,
          amplitude: 0.8,
          wavelength: 12,
          speed: 4,
          decay: 0.015,
        },
        contextId: "ctx_update",
      },
      // Color flicker based on time/position for shimmer
      {
        id: "update_color",
        type: "vfx.update.color_over_life",
        position: { x: 50, y: 250 },
        inputValues: {
          gradient: {
            stops: [
              { t: 0, color: { r: 0.3, g: 0.7, b: 3.5, a: 1 } },
              { t: 0.25, color: { r: 0.4, g: 0.9, b: 4.0, a: 1 } },
              { t: 0.5, color: { r: 0.2, g: 0.6, b: 3.0, a: 1 } },
              { t: 0.75, color: { r: 0.35, g: 0.8, b: 3.8, a: 1 } },
              { t: 1, color: { r: 0.3, g: 0.7, b: 3.5, a: 1 } },
            ],
          },
        },
        contextId: "ctx_update",
      },
      // Quad output
      {
        id: "output_quad",
        type: "vfx.output.quad",
        position: { x: 50, y: 50 },
        inputValues: { blendMode: 1 }, // Additive
        contextId: "ctx_output",
      },
    ],
    edges: [
      // === OPERATOR GRAPH: Get Index → Grid Position ===
      // index → float
      {
        id: "e1",
        from: { nodeId: "op_get_index", portId: "index" },
        to: { nodeId: "op_index_to_float", portId: "x" },
      },
      // float index → modulo (col)
      {
        id: "e2",
        from: { nodeId: "op_index_to_float", portId: "result" },
        to: { nodeId: "op_modulo", portId: "a" },
      },
      // float index → divide (for row)
      {
        id: "e3",
        from: { nodeId: "op_index_to_float", portId: "result" },
        to: { nodeId: "op_divide", portId: "a" },
      },
      // divide → floor (row)
      {
        id: "e4",
        from: { nodeId: "op_divide", portId: "result" },
        to: { nodeId: "op_floor", portId: "x" },
      },
      // col → offset → scale → x
      {
        id: "e5",
        from: { nodeId: "op_modulo", portId: "result" },
        to: { nodeId: "op_col_offset", portId: "a" },
      },
      {
        id: "e6",
        from: { nodeId: "op_col_offset", portId: "result" },
        to: { nodeId: "op_col_scale", portId: "a" },
      },
      // row → offset → scale → y
      {
        id: "e7",
        from: { nodeId: "op_floor", portId: "result" },
        to: { nodeId: "op_row_offset", portId: "a" },
      },
      {
        id: "e8",
        from: { nodeId: "op_row_offset", portId: "result" },
        to: { nodeId: "op_row_scale", portId: "a" },
      },
      // x, y → compose vec3
      {
        id: "e9",
        from: { nodeId: "op_col_scale", portId: "result" },
        to: { nodeId: "op_compose_pos", portId: "x" },
      },
      {
        id: "e10",
        from: { nodeId: "op_row_scale", portId: "result" },
        to: { nodeId: "op_compose_pos", portId: "y" },
      },
      // vec3 → set position
      {
        id: "e11",
        from: { nodeId: "op_compose_pos", portId: "result" },
        to: { nodeId: "init_position", portId: "position" },
      },
    ],
    variables: [],
    contexts: [
      {
        id: "ctx_spawn",
        type: "vfx.spawn",
        position: { x: 50, y: 50 },
        size: { width: 280, height: 120 },
        nodeIds: ["spawn_grid"],
        collapsed: false,
      },
      {
        id: "ctx_init",
        type: "vfx.initialize",
        position: { x: 50, y: 200 },
        size: { width: 280, height: 500 },
        nodeIds: [
          "init_position",
          "init_lifetime",
          "init_size",
          "init_color",
          "init_velocity",
        ],
        collapsed: false,
      },
      {
        id: "ctx_update",
        type: "vfx.update",
        position: { x: 50, y: 750 },
        size: { width: 280, height: 300 },
        nodeIds: ["update_wobble", "update_ripple", "update_color"],
        collapsed: false,
      },
      {
        id: "ctx_output",
        type: "vfx.output",
        position: { x: 50, y: 1100 },
        size: { width: 280, height: 120 },
        nodeIds: ["output_quad"],
        collapsed: false,
      },
    ],
    contextFlowEdges: [
      {
        id: "flow1",
        fromContextId: "ctx_spawn",
        fromPortId: "spawn_event",
        toContextId: "ctx_init",
        toPortId: "spawn_event",
      },
      {
        id: "flow2",
        fromContextId: "ctx_init",
        fromPortId: "particle",
        toContextId: "ctx_update",
        toPortId: "particle",
      },
      {
        id: "flow3",
        fromContextId: "ctx_update",
        fromPortId: "particle",
        toContextId: "ctx_output",
        toPortId: "particle",
      },
    ],
    contextSettings: {
      ctx_init: { capacity: 10000, boundsMode: "auto" },
    },
    capacity: { maxParticles: 10000, boundsMode: "auto" },
    simulation: { space: "world", deltaTimeMode: "deltaTime" },
    rendering: { sortMode: "none", blendMode: "additive" },
  };
  return regenerateGraphIds(template, name);
}

/**
 * Create a Singularity (Black Hole) VFX effect.
 *
 * Features:
 * - Accretion disk with particles spiraling inward
 * - Vortex rotation + gravitational pull
 * - HDR colors from orange (outer) to blinding white (inner)
 * - Size grows as particles approach event horizon
 * - Dramatic bloom effect with high color values
 */
